"use client";

import { useId, type ReactNode } from "react";
import { Button } from "../Button";
import { ModalSheet } from "./ModalSheet";

type Props = {
  isOpen: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  showCancelButton?: boolean;
  isSubmitting?: boolean;
  /** Raise above another open modal when nested inside one (see ModalSheet.elevated). */
  elevated?: boolean;
  /** "danger" makes confirm red (for destructive actions). "confirm" makes confirm primary (for safe actions). Default: "danger". */
  intent?: "danger" | "confirm";
  /** Optional icon override for the tile. Defaults to a danger/confirm glyph. */
  icon?: ReactNode;
  /** Optional icon override for the confirm button. Defaults to a small trash for danger, none otherwise. */
  confirmIcon?: ReactNode;
  onConfirm: () => void;
  onCancel?: () => void;
};

/**
 * Compact confirm/alert dialog built on the shared ModalSheet (so it inherits
 * the backdrop, entrance animation, focus trap, scroll-lock and escape-close).
 * A tinted icon tile sits beside the title + body, with right-aligned
 * Cancel / confirm actions — matching the design's confirm-modal look.
 */
export function ConfirmDialog({
  isOpen,
  title,
  body,
  confirmLabel,
  cancelLabel,
  showCancelButton = true,
  isSubmitting = false,
  elevated = false,
  intent = "danger",
  icon,
  confirmIcon,
  onConfirm,
  onCancel,
}: Props) {
  /*
   * The dialog's accessible name, the same way `Modal` and `Drawer` take
   * theirs: from the heading already on screen, never from a second copy of
   * the string.
   *
   * This one was the odd member of the family — it has had a real `<h3>` all
   * along, so it was the only dialog in the platform a screen-reader operator
   * could navigate INSIDE, and still the panel itself announced as an unnamed
   * "dialog". A heading is not a name.
   *
   * No `titleNames…` guard here, unlike `Modal`: `title` is a required string
   * and the `<h3>` is unconditional, so the id always resolves.
   */
  const titleId = useId();

  const tileClass =
    intent === "danger"
      ? "bg-[color:color-mix(in_oklab,var(--error-500)_12%,transparent)] text-[color:var(--error-500)]"
      : "bg-[color:color-mix(in_oklab,var(--accent-500)_12%,transparent)] text-[color:var(--accent-600,var(--accent-500))]";

  return (
    <ModalSheet
      isOpen={isOpen}
      size="sm"
      surface="card"
      variant="dialog"
      elevated={elevated}
      onClose={onCancel}
      labelledBy={titleId}
    >
      <div
        data-testid="confirm-dialog"
        className="flex items-start gap-3.5 px-[22px] pb-1.5 pt-[22px]"
      >
        <span
          aria-hidden="true"
          className={[
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px]",
            tileClass,
          ].join(" ")}
        >
          {icon ?? (intent === "danger" ? <TrashIcon /> : <CheckIcon />)}
        </span>
        <div className="min-w-0 flex-1 pt-0.5">
          <h3 id={titleId} className="text-[16.5px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
            {title}
          </h3>
          <p className="mt-[5px] text-[13px] leading-[1.55] text-[color:var(--text-muted)]">
            {body}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2.5 px-[22px] pb-5 pt-[18px]">
        {showCancelButton ? (
          <Button
            type="button"
            variant="outline"
            size="md"
            className="max-lg:min-h-10"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </Button>
        ) : null}
        <Button
          type="button"
          variant={intent === "danger" ? "danger" : "primary"}
          size="md"
          className="max-lg:min-h-10"
          onClick={onConfirm}
          disabled={isSubmitting}
          leadingIcon={confirmIcon ?? (intent === "danger" ? <TrashSmallIcon /> : undefined)}
          data-testid="confirm-dialog-confirm"
        >
          {confirmLabel}
        </Button>
      </div>
    </ModalSheet>
  );
}

function TrashIcon() {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function TrashSmallIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 11.1V12a10 10 0 1 1-5.9-9.1" />
      <path d="m9 11 3 3L22 4" />
    </svg>
  );
}
